import React from 'react';

import Icon from '@expo/vector-icons/MaterialIcons';
import { Dimensions, StyleSheet, View } from 'react-native';

import Link from 'components/link';
import { rootToaster } from 'components/toaster/refs';
import { Typography } from 'components/ui';
import { t } from 'state/intl/translations';
import { primitive, spacing } from 'styles/constants';
import { routes } from 'utils/routing';

const LONG_TOAST_DURATION = 7_000;
const TOAST_DELAY = 200;
const MOBILE_TOAST_WIDTH_RATIO = 0.9;

const getMobileToastWidth = () => Dimensions.get('window').width * MOBILE_TOAST_WIDTH_RATIO;

type IncentivesRemovedToastVariant = 'offer' | 'reward' | 'both';

const getToastCopy = (variant: IncentivesRemovedToastVariant) => {
  switch (variant) {
    case 'offer':
      return {
        title: t('cartHydrationOfferRemovedTitle', 'An offer was removed.'),
        showOffersLink: true,
        showRewardsLink: false,
      };
    case 'reward':
      return {
        title: t('cartHydrationRewardRemovedTitle', 'A reward was removed.'),
        showOffersLink: false,
        showRewardsLink: true,
      };
    case 'both':
      return {
        title: t('cartHydrationItemsRemovedTitle', 'Some items were removed.'),
        showOffersLink: true,
        showRewardsLink: true,
      };
  }
};

const getToastWidthStyle = (isMobile: boolean) => {
  if (isMobile) {
    // Native + mobile web: 90% of device width, centered.
    return {
      width: getMobileToastWidth(),
      maxWidth: getMobileToastWidth(),
      alignSelf: 'center' as const,
    };
  }

  // Desktop web: narrow and centered.
  return styles.desktopWebWidth;
};

const ToastMessage = ({
  showOffersLink,
  showRewardsLink,
}: {
  showOffersLink: boolean;
  showRewardsLink: boolean;
}) => {
  const offersLink = (
    <Link isInline to={routes.offers} color={primitive.$mayoEggWhite}>
      <Typography color={primitive.$mayoEggWhite} style={styles.link}>
        {t('cartHydrationOffersLink', 'offers')}
      </Typography>
    </Link>
  );

  const rewardsLink = (
    <Link isInline to={routes.rewards} color={primitive.$mayoEggWhite}>
      <Typography color={primitive.$mayoEggWhite} style={styles.link}>
        {t('cartHydrationRewardsLink', 'rewards')}
      </Typography>
    </Link>
  );

  return (
    <Typography variant="copyOne" color={primitive.$mayoEggWhite} style={styles.message}>
      {t('cartHydrationViewAvailablePrefix', 'View available ')}
      {showOffersLink ? offersLink : null}
      {showOffersLink && showRewardsLink
        ? t('cartHydrationOffersAndRewardsSeparator', ' & ')
        : null}
      {showRewardsLink ? rewardsLink : null}
      {t('cartHydrationViewAvailableSuffix', ' at your store')}
    </Typography>
  );
};

/**
 * Shows a single toast after offer and/or combo-reward removal during store-change hydration.
 * Does not modify the shared toaster API — renders via rootToaster directly.
 */
export const showIncentivesRemovedToast = ({
  removedOffer,
  removedComboReward,
  isMobile,
}: {
  removedOffer: boolean;
  removedComboReward: boolean;
  isMobile: boolean;
}) => {
  if (!removedOffer && !removedComboReward) {
    return;
  }

  const variant: IncentivesRemovedToastVariant =
    removedOffer && removedComboReward ? 'both' : removedOffer ? 'offer' : 'reward';

  const { title, showOffersLink, showRewardsLink } = getToastCopy(variant);
  const widthStyle = getToastWidthStyle(isMobile);

  setTimeout(() => {
    rootToaster.current?.show(
      <View style={[styles.container, widthStyle]}>
        <View style={styles.contentRow}>
          <Icon name="info-outline" size={spacing.$5} color={primitive.$white} />
          <View style={styles.textColumn}>
            <Typography variant="copyOne" color={primitive.$mayoEggWhite} style={styles.title}>
              {title}
            </Typography>
            <ToastMessage showOffersLink={showOffersLink} showRewardsLink={showRewardsLink} />
          </View>
        </View>
      </View>,
      {
        style: [styles.toast, widthStyle],
        placement: 'top',
        duration: LONG_TOAST_DURATION,
      }
    );
  }, TOAST_DELAY);
};

const styles = StyleSheet.create({
  toast: {
    backgroundColor: 'transparent',
    marginBottom: spacing.$2,
    padding: 0,
    margin: 0,
    justifyContent: 'center',
    alignSelf: 'center',
  },
  desktopWebWidth: {
    width: '100%',
    maxWidth: 420,
    minWidth: 0,
    alignSelf: 'center',
  },
  container: {
    backgroundColor: primitive.$black,
    padding: 16,
    borderRadius: 4,
    alignSelf: 'center',
    elevation: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  contentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  textColumn: {
    flex: 1,
    flexShrink: 1,
    gap: 4,
  },
  title: {
    fontFamily: primitive.fontFamily.$headers,
    fontSize: 16,
    fontWeight: 'bold',
    lineHeight: 20,
  },
  message: {
    fontFamily: primitive.fontFamily.$body,
    fontSize: 14,
    lineHeight: 18,
  },
  link: {
    fontFamily: primitive.fontFamily.$body,
    fontSize: 14,
    lineHeight: 18,
    textDecorationLine: 'underline',
  },
});
